import type { Brand } from "@borealis/aurora";
import { useId } from "react";
import { useAppConfig } from "../context/AppConfigProviderContext.tsx";

interface BrandLogoProps {
  brandOverride?: Brand;
  height?: number;
}

export const BrandLogo = ({ brandOverride, height }: BrandLogoProps) => {
  const { brand } = useAppConfig();
  const resolvedBrand = brandOverride ? brandOverride : brand;

  switch (resolvedBrand) {
    case "bilbasen":
      return <BilbasenLogo height={height} />;
    case "blocket":
      return <BlocketLogo height={height} />;
    case "dba":
      return <DBALogo height={height} />;
    case "finn":
      return <FinnLogo height={height} />;
    default:
      return null;
  }
};

const BilbasenLogo = ({ height }: { height?: number }) => {
  const id = useId();
  const clipId = `${id}-clip`;
  const maskId = `${id}-mask`;
  const h = height ?? 32;
  const w = Math.round((37 / 32) * h);
  return (
    <svg width={w} height={h} viewBox="0 0 37 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <title>Bilbasen</title>
      <g clipPath={`url(#${clipId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="m32.686 14.59 2.292-1.995c.544-.54-.382-.19-.93-.732-.546-.542-.437-.967-.984-.428l-.378-.77v-.027c-.636-2.718-1.741-5.462-2.965-7.327H6.35C5.132 5.169 4.032 7.9 3.393 10.615l-.056.759c-.547-.54-.255.008-.803.55-.547.542-1.994-.206-1.447.333l2.298 2.28v.385H0v12.916h5.085v-1.87H31.04v2.063h5.1V14.67h-3.454v-.08Z"
          fill="#002C5D"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M27.485 2H8.655c-.969 0-1.809.532-2.255 1.31h23.34A2.595 2.595 0 0 0 27.486 2ZM28.855 20.814H7.22v-.136c0-2.818 1.167-3.447 10.624-3.447 3.64 0 6.106.12 7.765.417 2.654.476 3.247 1.405 3.247 3.03v.136Zm3.826-10.198-.027-.002H3.423c-1.843 0-3.54 1.757-3.417 4.294L1.908 25.97h5.346v-1.428h21.633v1.428h5.298l1.931-10.912c.286-2.856-1.996-4.425-3.435-4.443Z"
          fill="#E9EEF4"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M33.936 14.605c0 1.068-.898 1.933-2.005 1.933-1.104 0-2.002-.865-2.002-1.933 0-1.066.898-1.93 2.002-1.93 1.107 0 2.005.864 2.005 1.93Z"
          fill="#fff"
        />
        <path
          clipRule="evenodd"
          d="M33.936 14.605c0 1.068-.898 1.933-2.005 1.933-1.104 0-2.002-.865-2.002-1.933 0-1.066.898-1.93 2.002-1.93 1.107 0 2.005.864 2.005 1.93Z"
          stroke="#C1C1C1"
          strokeWidth="1.881"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.975 14.605a1.943 1.943 0 0 1-1.953 1.933 1.942 1.942 0 0 1-1.951-1.933c0-1.066.875-1.93 1.95-1.93 1.08 0 1.954.864 1.954 1.93Z"
          fill="#fff"
        />
        <path
          clipRule="evenodd"
          d="M5.975 14.605a1.943 1.943 0 0 1-1.953 1.933 1.942 1.942 0 0 1-1.951-1.933c0-1.066.875-1.93 1.95-1.93 1.08 0 1.954.864 1.954 1.93Z"
          stroke="#C1C1C1"
          strokeWidth="1.881"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M24.847 10.614h-.944l-3.386-7.303h.944l3.386 7.303ZM29.364 10.614h-3.138l-3.45-7.303h3.14l3.448 7.303Z"
          fill="#2E527A"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M35.039 29.716h-2.886c-.602 0-1.094-.508-1.094-1.129v-1.589c0-.62 5.074-.62 5.074 0v1.59c0 .62-.492 1.128-1.094 1.128Z"
          fill="#002C5D"
        />
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="26" width="6" height="4">
          <path d="M0 29.865v-3.332h5.057v3.332H0Z" fill="#fff" />
        </mask>
        <g mask={`url(#${maskId})`}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3.98 29.865H1.095C.493 29.865 0 29.334 0 28.684l.011-1.664c0-.65 5.064-.65 5.064 0v1.664c0 .65-.493 1.181-1.095 1.181Z"
            fill="#002C5D"
          />
        </g>
      </g>
      <defs>
        <clipPath id={clipId}>
          <path fill="#fff" d="M0 2h36.202v28H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};

const BlocketLogo = ({ height }: { height?: number }) => {
  const id = useId();
  const clipId = `${id}-clip`;
  const h = height ?? 32;
  return (
    <svg width={h} height={h} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <title>Blocket</title>
      <g clipPath={`url(#${clipId})`} fillRule="evenodd" clipRule="evenodd">
        <path
          d="M27.9 4.1C25.011 1.15 20.505.03 16 0 11.494.03 6.989 1.151 4.1 4.1 1.15 6.99.03 11.495 0 16c.03 4.506 1.151 9.011 4.1 11.9C6.989 30.85 11.494 31.97 16 32c4.506-.03 9.011-1.151 11.9-4.1 2.949-2.889 4.07-7.394 4.1-11.9-.03-4.506-1.151-9.011-4.1-11.9Z"
          fill="#F71414"
        />
        <path
          d="M17.653 18.177c-.364.369-.932.509-1.5.513a2.909 2.909 0 0 1-.747-.099v-3.804a2.9 2.9 0 0 1 .747-.098c.568.004 1.136.144 1.5.512.372.361.513.925.517 1.488-.004.564-.145 1.127-.517 1.488Zm3.062-5.525c-.988-1-2.529-1.38-4.07-1.391a8.617 8.617 0 0 0-1.24.095V8.471h-3.64v13.647h4.88c1.541-.01 3.082-.39 4.07-1.391 1.009-.98 1.392-2.509 1.403-4.038-.01-1.528-.394-3.057-1.403-4.037Z"
          fill="#fff"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <path fill="#fff" d="M0 0h32v32H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};

const DBALogo = ({ height }: { height?: number }) => {
  const id = useId();
  const clipId = `${id}-clip`;
  const h = height ?? 19;
  const w = Math.round((48 / 19) * h);
  return (
    <svg width={w} height={h} viewBox="0 0 48 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <title>DBA</title>
      <g clipPath={`url(#${clipId})`} fillRule="evenodd" clipRule="evenodd" fill="#00098A">
        <path d="M10.917.088h2.818a.344.344 0 0 1 .355.33V17.94a.321.321 0 0 1-.107.23.352.352 0 0 1-.248.09h-2.404a.535.535 0 0 1-.473-.301l-.295-.744a6.451 6.451 0 0 1-4.11 1.478c-3.556 0-6.453-2.9-6.453-6.469 0-3.568 2.897-6.468 6.454-6.468a6.44 6.44 0 0 1 4.109 1.488V.408a.32.32 0 0 1 .107-.23.352.352 0 0 1 .247-.09Zm-7.38 12.146c0 1.789 1.577 3.239 3.518 3.239 1.94 0 3.517-1.45 3.517-3.24 0-1.788-1.576-3.238-3.517-3.238-1.941 0-3.518 1.45-3.518 3.239ZM21.28 7.24a6.409 6.409 0 0 1 4.11-1.487c3.557 0 6.453 2.908 6.453 6.475 0 3.566-2.896 6.465-6.453 6.465a6.441 6.441 0 0 1-4.11-1.487l-.295.744a.552.552 0 0 1-.473.3h-2.404a.352.352 0 0 1-.247-.088.32.32 0 0 1-.108-.231V.408a.321.321 0 0 1 .108-.231.352.352 0 0 1 .247-.09h2.818c.092-.002.18.03.248.09a.32.32 0 0 1 .107.23V7.24Zm0 4.978c0 1.788 1.577 3.238 3.518 3.238 1.942 0 3.518-1.45 3.518-3.238s-1.576-3.237-3.518-3.237c-1.94 0-3.517 1.45-3.517 3.237ZM45.034 6.22h2.611c.092-.004.181.028.248.088.066.06.105.144.107.231v11.446a.322.322 0 0 1-.107.23.352.352 0 0 1-.248.09h-2.404a.535.535 0 0 1-.473-.302l-.295-.744a6.449 6.449 0 0 1-4.11 1.479c-3.556 0-6.453-2.901-6.453-6.471 0-3.57 2.897-6.472 6.454-6.472 1.512.001 2.972.527 4.109 1.48l.147-.745a.431.431 0 0 1 .414-.31Zm-4.07 9.278c1.932 0 3.509-1.442 3.519-3.222v-.038c-.01-1.78-1.577-3.221-3.518-3.221-1.941 0-3.518 1.45-3.518 3.24 0 1.79 1.587 3.24 3.518 3.24Z" />
      </g>
      <defs>
        <clipPath id={clipId}>
          <path fill="#fff" d="M0 0h48v18.801H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};

const FinnLogo = ({ height }: { height?: number }) => {
  const id = useId();
  const clipId = `${id}-clip`;
  const h = height ?? 25;
  const w = Math.round((48 / 25) * h);
  return (
    <svg width={w} height={h} viewBox="0 0 48 25" fill="none" xmlns="http://www.w3.org/2000/svg">
      <title>FINN</title>
      <g clipPath={`url(#${clipId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.619 22.643V2.357c0-.4.323-.726.72-.726h6.368c7.988 0 14.487 6.548 14.487 14.596v7.142H2.339a.724.724 0 0 1-.72-.726Zm44.762 0c0 .4-.323.726-.72.726H24.813V2.357c0-.4.323-.726.72-.726h20.128c.397 0 .72.326.72.726v20.286ZM45.661 0H25.533a2.35 2.35 0 0 0-2.339 2.357V9.14C20.578 3.735 15.068 0 8.707 0H2.339A2.35 2.35 0 0 0 0 2.357v20.286A2.35 2.35 0 0 0 2.34 25h43.32A2.35 2.35 0 0 0 48 22.643V2.357A2.35 2.35 0 0 0 45.66 0Z"
          fill="#fff"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M46.381 22.643V2.357c0-.4-.323-.726-.72-.726H25.533a.724.724 0 0 0-.72.726v21.012H45.66c.397 0 .72-.326.72-.726Z"
          fill="#06BEFB"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.707 1.631H2.339a.724.724 0 0 0-.72.726v20.286c0 .4.323.726.72.726h20.855v-7.142c0-8.048-6.499-14.596-14.487-14.596Z"
          fill="#0063FB"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <path fill="#fff" d="M0 0h48v25H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};
